// Kunden-Sicht: Bestellungen
// - Oben: aktiver Warenkorb (status=draft) — Items bearbeiten/löschen,
//   "Bestellung absenden" Button
// - Unten: Bestellhistorie (submitted / converted / cancelled) mit Status-Anzeige
import { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { ShoppingBag, Send, Trash2, Plus, Minus, Clock, CheckCircle2, XCircle, Package, Box, ChevronDown, ChevronUp, Search, Download } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const fmtEur = (v) => new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
}).format(Number(v || 0));

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
  : '–';

const STATUS_META = {
  draft: { label: 'Warenkorb (offen)', color: 'bg-gray-100 text-gray-600', icon: Box },
  submitted: { label: 'Gesendet — wartet auf Bestätigung', color: 'bg-amber-50 text-amber-700', icon: Clock },
  converted: { label: 'In Bearbeitung — Auftrag erstellt', color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
  cancelled: { label: 'Storniert', color: 'bg-red-50 text-red-700', icon: XCircle },
};

// Innen-Status der internen Aufträge → Anzeige fürs Kundendashboard.
// Wenn shop_order.linkedOrder.status gesetzt ist, überschreibt dieses
// Label das obige „converted"-Mapping — der Kunde sieht so live, in
// welcher Phase sein Auftrag steckt (Bearbeitung → Produktion → Erledigt).
const INTERNAL_STATUS_META = {
  new:         { label: 'Auftrag aufgenommen',     color: 'bg-blue-50 text-blue-700',       icon: CheckCircle2 },
  in_progress: { label: 'In Bearbeitung',          color: 'bg-amber-50 text-amber-700',     icon: Clock },
  production:  { label: 'In Produktion',           color: 'bg-purple-50 text-purple-700',   icon: Clock },
  done:        { label: 'Auftrag erledigt',        color: 'bg-emerald-50 text-emerald-700', icon: CheckCircle2 },
};

function effectiveStatusMeta(order) {
  // Wenn ein verknüpfter Auftrag existiert + interner Status gesetzt ist,
  // zeigen wir DEN Status (nicht mehr nur „converted").
  const linkedStatus = order?.linkedOrder?.status;
  if (linkedStatus && INTERNAL_STATUS_META[linkedStatus]) {
    return INTERNAL_STATUS_META[linkedStatus];
  }
  return STATUS_META[order.status] || STATUS_META.draft;
}

function CartItemRow({ item, onUpdate, onRemove, busy }) {
  const [qty, setQty] = useState(item.quantity);
  const lineNet = (Number(item.quantity) || 0) * (Number(item.single_price_net) || 0);

  useEffect(() => { setQty(item.quantity); }, [item.quantity]);

  const commit = (n) => {
    const next = Math.max(0, Number(n) || 0);
    setQty(next);
    onUpdate(item.articleId, next);
  };

  return (
    <tr data-testid={`cart-row-${item.articleId}`}>
      <td className="px-3 py-2">
        <div className="text-xs font-medium text-[#32373c]">{item.title}</div>
        <div className="text-[10px] text-gray-400 font-mono">{item.articleNumber || '–'}</div>
      </td>
      <td className="px-3 py-2 text-xs text-gray-500">{item.groupName || '–'}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => commit((Number(qty) || 0) - 1)}
            disabled={busy}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            data-testid={`btn-qty-minus-${item.articleId}`}
          ><Minus className="w-3 h-3" /></button>
          <input
            type="number"
            value={qty}
            min="0"
            onChange={e => setQty(e.target.value)}
            onBlur={e => commit(e.target.value)}
            disabled={busy}
            className="w-14 px-1.5 py-0.5 border border-gray-200 rounded text-xs text-center tabular-nums"
            data-testid={`input-qty-${item.articleId}`}
          />
          <button
            onClick={() => commit((Number(qty) || 0) + 1)}
            disabled={busy}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
            data-testid={`btn-qty-plus-${item.articleId}`}
          ><Plus className="w-3 h-3" /></button>
          <span className="text-[10px] text-gray-400 ml-1">{item.unit}</span>
        </div>
      </td>
      <td className="px-3 py-2 text-right text-xs tabular-nums text-gray-600">{fmtEur(item.single_price_net)}</td>
      <td className="px-3 py-2 text-right text-xs tabular-nums font-medium text-[#32373c]">{fmtEur(lineNet)}</td>
      <td className="px-3 py-2 text-right">
        <button
          onClick={() => onRemove(item.articleId)}
          disabled={busy}
          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
          data-testid={`btn-remove-${item.articleId}`}
        ><Trash2 className="w-3.5 h-3.5" /></button>
      </td>
    </tr>
  );
}

function OrderHistoryCard({ order, onOpen }) {
  const meta = effectiveStatusMeta(order);
  const Icon = meta.icon;
  return (
    <button
      onClick={() => onOpen(order)}
      className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 hover:border-[#9ec043]/40 hover:shadow-sm transition-all"
      data-testid={`history-order-${order.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-xs text-gray-400 font-mono">#{order.id.slice(0, 8)}</div>
          <div className="text-sm font-medium text-[#32373c]">
            Abgesendet: {fmtDate(order.submittedAt)}
          </div>
          {order.convertedAt && (
            <div className="text-[11px] text-emerald-700">
              Auftrag bestätigt: {fmtDate(order.convertedAt)}
            </div>
          )}
          {order.linkedOrder?.confirmedDeliveryDate && (
            <div className="text-[11px] text-emerald-700 font-medium" data-testid={`history-delivery-${order.id}`}>
              Liefertermin: {new Date(order.linkedOrder.confirmedDeliveryDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </div>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium ${meta.color}`}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
      </div>
      <div className="space-y-1 mb-2">
        {(order.items || []).slice(0, 3).map((it, idx) => (
          <div key={idx} className="flex justify-between text-xs text-gray-600">
            <span>{it.quantity}× {it.title}</span>
            <span className="tabular-nums">{fmtEur((it.quantity || 0) * (it.single_price_net || 0))}</span>
          </div>
        ))}
        {(order.items || []).length > 3 && (
          <div className="text-[11px] text-gray-400">+ {order.items.length - 3} weitere Artikel</div>
        )}
      </div>
      <div className="flex justify-between border-t border-gray-100 pt-2 text-xs">
        <span className="text-gray-500">Summe (netto)</span>
        <span className="tabular-nums font-semibold text-[#32373c]">{fmtEur(order.totalNet)}</span>
      </div>
    </button>
  );
}

function SystemCheckSnapshot({ snapshot }) {
  const [open, setOpen] = useState(false);
  const fmtDate = ts => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return ts; }
  };
  return (
    <div className="border-t border-gray-100" data-testid="system-check-snapshot">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full px-3 py-1.5 text-[11px] text-[#7ca735] hover:bg-[#9ec043]/10 flex items-center justify-between"
        data-testid="btn-toggle-system-check"
      >
        <span className="flex items-center gap-1.5">
          <Box className="w-3 h-3" />
          Systemprüfung & Trackerbelegung {open ? 'ausblenden' : 'anzeigen'}
        </span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="px-3 py-2.5 bg-[#9ec043]/5 text-[11px] text-gray-700 space-y-2.5">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-gray-500">
            {snapshot.checkedAt && <span>Geprüft am {fmtDate(snapshot.checkedAt)}</span>}
            {snapshot.summary?.totalModules > 0 && (
              <>
                <span>Module: <strong className="text-[#32373c]">{snapshot.summary.totalModules}</strong></span>
                <span>PV-Leistung: <strong className="text-[#32373c]">{snapshot.summary.totalPvKw?.toFixed(2)} kWp</strong></span>
              </>
            )}
            <span>Modus: <strong className="text-[#32373c]">{snapshot.mode === 'strings' ? 'Strings' : 'Modulfelder'}</strong></span>
          </div>
          {/* Eingabe-Werte */}
          {snapshot.entries?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                Eingegebene {snapshot.mode === 'strings' ? 'Strings' : 'Modulfelder'}
              </div>
              <div className="space-y-1">
                {snapshot.entries.map((e, i) => (
                  <div key={i} className="bg-white border border-gray-100 rounded px-2 py-1.5">
                    <div className="font-medium text-[#32373c]">
                      {e.name || `${snapshot.mode === 'strings' ? 'String' : 'Feld'} ${i + 1}`}
                      {e.moduleType && <span className="text-gray-400 font-normal"> · {e.moduleType}</span>}
                    </div>
                    <div className="text-gray-500 text-[10px] grid grid-cols-2 sm:grid-cols-4 gap-x-3">
                      <span>Module: <strong className="text-gray-700">{e.moduleCount}</strong></span>
                      <span>Voc: <strong className="text-gray-700">{e.vocPerModule} V</strong></span>
                      <span>Leistung: <strong className="text-gray-700">{e.powerWpPerModule} Wp</strong></span>
                      <span>Imp: <strong className="text-gray-700">{e.nominalCurrentPerModule} A</strong></span>
                      <span>Ausrichtung: <strong className="text-gray-700">{e.orientation}</strong></span>
                      {e.vmpPerModule && <span>Vmp: <strong className="text-gray-700">{e.vmpPerModule} V</strong></span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Trackerbelegung */}
          {snapshot.assignment?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                Trackerbelegung
              </div>
              <div className="space-y-1">
                {snapshot.assignment.map(t => (
                  <div key={t.id} className="bg-white border border-gray-100 rounded px-2 py-1.5 grid grid-cols-[44px_1fr] gap-2 items-start">
                    <div className="font-mono text-[#7ca735] font-semibold tabular-nums">{t.id}</div>
                    {(!t.strings || t.strings.length === 0) ? (
                      <span className="text-gray-400 italic">— frei —</span>
                    ) : (
                      <div className="space-y-0.5">
                        {t.strings.map((s, i) => (
                          <div key={i} className="text-gray-700">
                            <strong>{s.name}</strong> · {s.moduleCount} Module · {s.powerKw?.toFixed(2)} kWp
                            {s.stringVocCold && <> · Voc kalt {s.stringVocCold.toFixed(0)} V</>}
                            {t.strings.length > 1 && <span className="text-[10px] text-purple-500 ml-1">(parallel)</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OrderDetailModal({ order, onClose }) {
  const [openItems, setOpenItems] = useState(new Set());
  if (!order) return null;
  const meta = effectiveStatusMeta(order);
  const Icon = meta.icon;
  const toggleItem = (id) => setOpenItems(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="customer-order-detail-modal">
        <div className="p-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-[#32373c]">Bestellung #{order.id.slice(0, 8)}</h3>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${meta.color}`}>
                <Icon className="w-3 h-3" /> {meta.label}
              </span>
            </div>
            <div className="text-[11px] text-gray-400">
              Abgesendet: {fmtDate(order.submittedAt)}
              {order.convertedAt && ` · Bestätigt: ${fmtDate(order.convertedAt)}`}
            </div>
            {order.linkedOrder?.confirmedDeliveryDate && (
              <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium" data-testid="detail-confirmed-delivery-date">
                Liefertermin: {new Date(order.linkedOrder.confirmedDeliveryDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
              </div>
            )}
            {order.commission && (
              <div className="mt-2 text-xs text-gray-600">
                <strong className="text-gray-500">Kommission / Bauvorhaben:</strong> {order.commission}
              </div>
            )}
            {order.deliveryAddressMode === 'custom' && order.deliveryAddress && (
              <div className="mt-1 text-xs text-gray-600">
                <strong className="text-gray-500">Lieferadresse:</strong>
                <pre className="font-sans inline ml-1 whitespace-pre-wrap">{order.deliveryAddress}</pre>
              </div>
            )}
            {order.customerNote && (
              <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded p-2 max-w-md">
                <strong className="text-gray-500">Mitteilung an EES:</strong> {order.customerNote}
              </div>
            )}
            {/* Verknüpfte Auftragsbestätigung ODER Lieferschein (Easybill) —
                Punkt 3 Runde 3 (28.02.2026): Bei einer Bestellung (orderKind
                = 'bestellung', Direktflow für Zubehör) zeigen wir Lieferschein
                statt Auftragsbestätigung — genau das ist die Vereinfachung. */}
            {order.linkedOrder?.easybillDocumentId && (() => {
              const isBestellung = order.linkedOrder?.orderKind === 'bestellung';
              const title = isBestellung ? 'Lieferschein' : 'Auftragsbestätigung';
              const testid = isBestellung ? 'order-delivery-block' : 'order-confirmation-block';
              return (
                <div className="mt-3 rounded-lg border border-[#9ec043]/40 bg-[#9ec043]/5 p-3" data-testid={testid}>
                  <div className="text-[11px] uppercase tracking-wide text-[#7ca735] font-semibold mb-1">
                    {title}
                  </div>
                  <div className="text-xs text-[#32373c] flex flex-wrap items-center gap-2">
                    {order.linkedOrder.easybillDocumentNumber ? (
                      <>Nr. <strong className="font-semibold">{order.linkedOrder.easybillDocumentNumber}</strong></>
                    ) : (
                      <span className="text-gray-500 italic">Wird noch in Easybill finalisiert — die Belegnummer erscheint, sobald der Beleg freigegeben ist.</span>
                    )}
                    {order.linkedOrder.confirmedDeliveryDate && (
                      <span className="ml-2 text-[11px] text-gray-600">
                        Liefertermin: <strong>{new Date(order.linkedOrder.confirmedDeliveryDate).toLocaleDateString('de-DE')}</strong>
                      </span>
                    )}
                    {order.linkedOrder.easybillDocumentNumber && (
                      <a
                        href={`${API_BASE}/api/shop/orders/my/${order.id}/document.pdf`}
                        target="_blank" rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#9ec043] text-white hover:bg-[#7ca735] text-xs font-medium"
                        data-testid="btn-download-confirmation">
                        <Download className="w-3.5 h-3.5" /> Als PDF öffnen
                      </a>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded text-gray-400" data-testid="btn-close-detail">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {(order.items || []).map((it, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`detail-item-${idx}`}>
              <div className="p-3 flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] text-gray-400 font-mono">{it.articleNumber || '–'}</div>
                  <div className="text-sm font-medium text-[#32373c]">{it.title}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {it.quantity}× {it.unit || 'Stück'} · {fmtEur(it.single_price_net)} netto / {it.unit}
                  </div>
                </div>
                <div className="text-sm font-semibold text-[#7ca735] tabular-nums whitespace-nowrap">
                  {fmtEur((it.quantity || 0) * (it.single_price_net || 0))}
                </div>
              </div>
              {it.description && (
                <>
                  <button
                    onClick={() => toggleItem(idx)}
                    className="w-full px-3 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50 border-t border-gray-100 flex items-center justify-between"
                    data-testid={`btn-toggle-detail-${idx}`}
                  >
                    <span>Technische Details {openItems.has(idx) ? 'ausblenden' : 'anzeigen'}</span>
                    {openItems.has(idx) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {openItems.has(idx) && (
                    <pre className="px-3 py-2 text-[11px] text-gray-600 bg-gray-50 whitespace-pre-wrap font-sans leading-relaxed">
                      {it.description}
                    </pre>
                  )}
                </>
              )}
              {it.systemCheck && <SystemCheckSnapshot snapshot={it.systemCheck} />}
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-between text-sm">
          <span className="text-gray-500">Gesamtsumme (netto)</span>
          <span className="tabular-nums font-bold text-[#32373c]">{fmtEur(order.totalNet)}</span>
        </div>
      </div>
    </div>
  );
}

export default function MyOrdersPage() {
  const [cart, setCart] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);
  const [commission, setCommission] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [deliveryMode, setDeliveryMode] = useState('standard');
  const [delivery, setDelivery] = useState({
    company: '',  // Firma / Name
    street: '',   // Straße + Hausnummer
    cityLine: '', // PLZ + Ort
    phone: '',    // Tel. Nr. — Pflicht bei abweichender Lieferadresse
  });
  const [historyQuery, setHistoryQuery] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    const [cartR, listR] = await Promise.all([
      authFetch('/api/shop/orders/draft'),
      authFetch('/api/shop/orders/my'),
    ]);
    if (cartR.ok) setCart(await cartR.json());
    if (listR.ok) {
      const d = await listR.json();
      // Filter out draft from history (cart is shown separately)
      setHistory((d.orders || []).filter(o => o.status !== 'draft'));
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateQty = async (articleId, qty) => {
    setBusy(true);
    const r = await authFetch(`/api/shop/orders/draft/items/${articleId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantity: qty }),
    });
    setBusy(false);
    if (r.ok) setCart(await r.json());
    else toast.error('Aktualisierung fehlgeschlagen');
  };

  const removeItem = (articleId) => updateQty(articleId, 0);

  const submit = async () => {
    if (!cart || !cart.items || cart.items.length === 0) {
      toast.error('Warenkorb ist leer');
      return;
    }
    if (deliveryMode === 'custom') {
      const missing = [];
      if (!delivery.company.trim()) missing.push('Firma/Name');
      if (!delivery.street.trim())  missing.push('Straße');
      if (!delivery.cityLine.trim()) missing.push('PLZ/Ort');
      if (!delivery.phone.trim())   missing.push('Telefonnummer');
      // Telefon-Validierung: mindestens 5 Ziffern (international+Format-Toleranz)
      const phoneDigits = delivery.phone.replace(/\D/g, '');
      if (delivery.phone.trim() && phoneDigits.length < 5) {
        toast.error('Bitte eine gültige Telefonnummer eingeben (mind. 5 Ziffern).');
        return;
      }
      if (missing.length > 0) {
        toast.error(`Bei abweichender Lieferadresse sind Pflichtfelder fehlen: ${missing.join(', ')}.`);
        return;
      }
    }
    if (!window.confirm('Bestellung verbindlich an EES senden?')) return;
    setBusy(true);
    // Adresse als zusammenhängender Block + separates phone-Feld senden,
    // damit Innendienst-/Spedition direkt ans Telefon kommt.
    const customAddress = deliveryMode === 'custom'
      ? `${delivery.company}\n${delivery.street}\n${delivery.cityLine}\nTel.: ${delivery.phone}`
      : null;
    const r = await authFetch(`/api/shop/orders/${cart.id}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        commission: commission.trim() || null,
        customerNote: customerNote.trim() || null,
        deliveryAddressMode: deliveryMode,
        deliveryAddress: customAddress,
        deliveryPhone: deliveryMode === 'custom' ? delivery.phone.trim() : null,
      }),
    });
    setBusy(false);
    if (r.ok) {
      toast.success('Bestellung gesendet! Du wirst informiert, sobald sie bearbeitet wird.');
      setCommission(''); setCustomerNote('');
      setDeliveryMode('standard');
      setDelivery({ company: '', street: '', cityLine: '', phone: '' });
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Bestellung konnte nicht gesendet werden');
    }
  };

  const cartItems = cart?.items || [];
  const cartTotal = Number(cart?.totalNet || 0);

  return (
    <div className="p-6 max-w-5xl" data-testid="my-orders-page">
      <div className="flex items-center gap-2 mb-6">
        <ShoppingBag className="w-6 h-6 text-[#9ec043]" />
        <h1 className="text-2xl font-semibold text-[#32373c]">Bestellungen</h1>
      </div>

      {/* AKTIVER WARENKORB */}
      <section className="mb-8" data-testid="cart-section">
        <h2 className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
          <Box className="w-4 h-4 text-[#9ec043]" />
          Aktuelle Bestellung (Warenkorb)
        </h2>
        {loading ? (
          <div className="text-xs text-gray-400">Lade …</div>
        ) : cartItems.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center" data-testid="empty-cart">
            <p className="text-sm text-gray-500 mb-2">Dein Warenkorb ist leer.</p>
            <button
              onClick={() => navigate('/dashboard/shop')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735]"
              data-testid="btn-go-to-catalog"
            >
              Zum Produktkatalog
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Artikel</th>
                  <th className="text-left px-3 py-2 font-medium">Gruppe</th>
                  <th className="text-left px-3 py-2 font-medium">Menge</th>
                  <th className="text-right px-3 py-2 font-medium">Stückpreis</th>
                  <th className="text-right px-3 py-2 font-medium">Summe netto</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cartItems.map(it => (
                  <CartItemRow key={it.articleId} item={it} onUpdate={updateQty} onRemove={removeItem} busy={busy} />
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="4" className="text-right px-3 py-2 text-xs font-medium text-gray-500">Gesamt netto</td>
                  <td className="text-right px-3 py-2 text-sm font-bold text-[#32373c] tabular-nums" data-testid="cart-total-net">{fmtEur(cartTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            {/* Systemprüfungs-Snapshots pro Item, falls vorhanden — sichtbar
                bereits im Warenkorb, damit der Kunde direkt sieht, dass die
                Auslegung mit dem Artikel verknüpft wurde. */}
            {cartItems.some(it => it.systemCheck) && (
              <div className="px-3 pb-3 space-y-2">
                {cartItems.filter(it => it.systemCheck).map(it => (
                  <div key={`snap-${it.articleId}`} className="border border-[#9ec043]/30 rounded-lg overflow-hidden">
                    <div className="px-3 py-1.5 bg-[#9ec043]/10 text-[11px] font-semibold text-[#7ca735]">
                      {it.title} — gespeicherte Systemprüfung
                    </div>
                    <SystemCheckSnapshot snapshot={it.systemCheck} />
                  </div>
                ))}
              </div>
            )}
            <div className="p-4 border-t border-gray-100 bg-gray-50/30 space-y-3">
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                  Kommission / Bauvorhaben
                </label>
                <input
                  value={commission}
                  onChange={e => setCommission(e.target.value)}
                  placeholder="z. B. BV Müller, Bauleitnummer 2026/042"
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                  data-testid="input-commission"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                  Lieferadresse
                </label>
                <div className="flex flex-wrap gap-3 mb-2 text-xs">
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={deliveryMode === 'standard'}
                      onChange={() => setDeliveryMode('standard')}
                      className="accent-[#9ec043]"
                      data-testid="radio-delivery-standard"
                    />
                    <span>Standardadresse</span>
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      checked={deliveryMode === 'custom'}
                      onChange={() => setDeliveryMode('custom')}
                      className="accent-[#9ec043]"
                      data-testid="radio-delivery-custom"
                    />
                    <span>Abweichende Lieferadresse</span>
                  </label>
                </div>
                {deliveryMode === 'custom' && (
                  <div className="space-y-1.5" data-testid="custom-address-fields">
                    <input
                      type="text"
                      value={delivery.company}
                      onChange={e => setDelivery(d => ({ ...d, company: e.target.value }))}
                      placeholder="Firma / Name"
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                      data-testid="input-delivery-company"
                      required
                    />
                    <input
                      type="text"
                      value={delivery.street}
                      onChange={e => setDelivery(d => ({ ...d, street: e.target.value }))}
                      placeholder="Straße, Hausnummer"
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                      data-testid="input-delivery-street"
                      required
                    />
                    <input
                      type="text"
                      value={delivery.cityLine}
                      onChange={e => setDelivery(d => ({ ...d, cityLine: e.target.value }))}
                      placeholder="PLZ, Ort"
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                      data-testid="input-delivery-city"
                      required
                    />
                    <input
                      type="tel"
                      value={delivery.phone}
                      onChange={e => setDelivery(d => ({ ...d, phone: e.target.value }))}
                      placeholder="Tel. Nummer (Pflicht für Avisierung)"
                      className="w-full px-3 py-1.5 border border-amber-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043] bg-amber-50/40"
                      data-testid="input-delivery-phone"
                      required
                    />
                    <p className="text-[10px] text-amber-700">
                      Die Telefonnummer ist Pflicht — Speditionen brauchen sie für Avisierung und Rückfragen.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                  Mitteilung an EES (z. B. Wunschliefertermin, Eilbestellung, Sonderwünsche)
                </label>
                <textarea
                  value={customerNote}
                  onChange={e => setCustomerNote(e.target.value)}
                  rows={3}
                  placeholder="optional"
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                  data-testid="input-customer-note"
                />
              </div>
            </div>
            <div className="px-4 py-2.5 border-t border-gray-100 bg-amber-50/40 text-[11px] text-amber-800 leading-relaxed" data-testid="checkout-disclaimer">
              <strong>Hinweis:</strong> Die Auftragsbestätigung wird separat zugestellt. Transportkosten werden im Zuge der Auftragsbestätigung separat berechnet.
            </div>
            <div className="p-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 bg-gray-50/50">
              <button
                onClick={() => navigate('/dashboard/shop')}
                className="text-xs text-gray-500 hover:text-[#9ec043]"
                data-testid="btn-add-more"
              >
                + Weitere Artikel hinzufügen
              </button>
              <button
                onClick={submit}
                disabled={busy || cartItems.length === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#9ec043] text-white text-sm font-medium hover:bg-[#7ca735] disabled:opacity-50"
                data-testid="btn-submit-order"
              >
                <Send className="w-3.5 h-3.5" />
                {busy ? 'Sende …' : 'Bestellung absenden'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* BESTELLHISTORIE */}
      <section data-testid="history-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <Package className="w-4 h-4 text-[#9ec043]" />
            Bestellhistorie
            {history.length > 0 && <span className="text-xs text-gray-400">({history.length})</span>}
          </h2>
          {history.length > 0 && (
            <div className="relative w-full sm:w-72">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={historyQuery}
                onChange={e => setHistoryQuery(e.target.value)}
                placeholder="Suche: Artikel, Kommission, Lieferadresse …"
                className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                data-testid="input-history-search"
              />
            </div>
          )}
        </div>
        {(() => {
          const q = historyQuery.trim().toLowerCase();
          const filtered = !q ? history : history.filter(o => {
            const haystack = [
              o.commission,
              o.deliveryAddress,
              o.customerNote,
              o.id,
              ...(o.items || []).flatMap(it => [it.title, it.articleNumber, it.groupName]),
            ].filter(Boolean).join(' ').toLowerCase();
            return haystack.includes(q);
          });
          if (history.length === 0) {
            return (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-400 text-center" data-testid="empty-history">
                Du hast noch keine Bestellungen abgesendet.
              </div>
            );
          }
          if (filtered.length === 0) {
            return (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-400 text-center" data-testid="empty-history-search">
                Keine Bestellung passt zur Suche „{historyQuery}".
              </div>
            );
          }
          return (
            <div className="space-y-3">
              {filtered.map(o => <OrderHistoryCard key={o.id} order={o} onOpen={setDetailOrder} />)}
            </div>
          );
        })()}
      </section>

      <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />
    </div>
  );
}
